import axios, { AxiosInstance, AxiosError } from 'axios';
import { useAuthStore } from '@/stores/auth.store';
import { isGatewayDownStatus, markServiceUpdating } from './service-status';
import { useSyncStore } from '@/stores/sync.store';
import { getUploadUrl, MAX_ATTACHMENT_SIZE, formatBytes, extractErrorMessage } from './utils';
import { tokenManager } from './token-manager';
import { recordFailedRequest } from './diagnostics';
import { getTraceHeaders, recordApiEvent } from './event-journal';

export type { ApiResponse, ValidationErrors, DebugInfo, PaginatedData } from '@/shared/schemas';
import type { ApiResponse, PaginatedData } from '@/shared/schemas';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3006/api';

const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor - Tự động gắn JWT token từ Zustand store vào mọi request
apiClient.interceptors.request.use(
  (config) => {
    const token = useAuthStore.getState().accessToken;
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Chuẩn Feedback v2: request id mới + mã phiên tab
    const trace = getTraceHeaders();
    config.headers['x-request-id'] = trace['x-request-id'];
    config.headers['x-session-id'] = trace['x-session-id'];
    (config as any).__apvStartedAt = Date.now();
    const reqMethod = String(config.method || 'GET').toLowerCase();
    const reqUrl = String(config.url || '');
    const isTrackedMutation =
      ['post', 'put', 'patch', 'delete'].includes(reqMethod) &&
      !reqUrl.includes('/auth/') &&
      !(config as any)._retry;
    if (isTrackedMutation) {
      (config as any).__apvSyncTracked = true;
      useSyncStore.getState().beginTask('Đang lưu dữ liệu...');
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - Xử lý lỗi và tự động refresh token
apiClient.interceptors.response.use(
  (response) => {
    const cfg: any = response.config || {};
    if (cfg.__apvSyncTracked) {
      cfg.__apvSyncTracked = false;
      useSyncStore.getState().completeTask(true);
    }
    // REST ghi dữ liệu thành công → footer / Lịch sử cập nhật tải lại (cùng sự kiện với tRPC mutation)
    if (['post', 'put', 'patch', 'delete'].includes(String(cfg.method || '').toLowerCase()) && typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('apv:data-mutated'));
    }
    recordApiEvent({
      method: cfg.method || 'GET',
      url: `${cfg.baseURL || ''}${cfg.url || ''}`,
      status: response.status,
      durationMs: Date.now() - Number(cfg.__apvStartedAt || Date.now()),
      requestId: (response.headers?.['x-request-id'] as string) || cfg.headers?.['x-request-id'],
    });
    return response;
  },
  async (error: AxiosError<ApiResponse>) => {
    const originalRequest = error.config as any;
    if (isGatewayDownStatus(error.response?.status)) markServiceUpdating();
    recordApiEvent({
      method: originalRequest?.method || 'GET',
      url: `${originalRequest?.baseURL || ''}${originalRequest?.url || ''}`,
      status: error.response?.status ?? 'NETWORK_ERROR',
      durationMs: Date.now() - Number(originalRequest?.__apvStartedAt || Date.now()),
      requestId: (error.response?.headers?.['x-request-id'] as string) || originalRequest?.headers?.['x-request-id'],
      message: (error.response?.data as any)?.message || error.message,
    });

    if (error.response?.status === 401 && !originalRequest._retry) {
      if (import.meta.env.DEV) {
        console.error('[API_CLIENT] 401 Unauthorized for URL:', originalRequest.url);
        console.error('[API_CLIENT] Auth header was:', originalRequest.headers?.Authorization);
        console.error('[API_CLIENT] Error message:', error.response?.data?.message);
      }
      
      // Không retry cho login hoặc refresh-token endpoint
      if (originalRequest.url?.includes('/auth/login') || originalRequest.url?.includes('/auth/refresh-token')) {
        return Promise.reject(error);
      }

      const errorMessage = error.response?.data?.message || '';

      // Kiểm tra nếu tài khoản bị vô hiệu hóa -> force logout ngay, không cần refresh
      const isAccountDisabled =
        errorMessage.includes('vô hiệu hóa') ||
        errorMessage.includes('Phiên đăng nhập không hợp lệ');

      if (isAccountDisabled) {
        tokenManager.handleAuthFailure(errorMessage);
        return Promise.reject(error);
      }

      originalRequest._retry = true;

      const newToken = await tokenManager.refreshAccessToken();

      if (newToken) {
        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return apiClient(originalRequest);
      }

      if (originalRequest?.__apvSyncTracked) {
        originalRequest.__apvSyncTracked = false;
        useSyncStore.getState().completeTask(false);
      }
      return Promise.reject(error);
    }

    // Xử lý các lỗi khác
    if (error.response) {
      if (originalRequest?.__apvSyncTracked) {
        originalRequest.__apvSyncTracked = false;
        useSyncStore.getState().completeTask(false);
      }
      const errorResponse = error.response.data;
      recordFailedRequest({
        method: originalRequest?.method || 'GET',
        url: String(originalRequest?.url || ''),
        status: error.response.status,
        message: errorResponse?.message || error.message,
      });
      // Log errors in development only
      if (import.meta.env.DEV) {
        console.error('API Error:', errorResponse);
      }
    } else if (error.request) {
      const method = String(originalRequest?.method || 'GET').toUpperCase();
      if (method === 'GET' && originalRequest && !originalRequest._netRetry) {
        originalRequest._netRetry = true;
        await new Promise((resolve) => setTimeout(resolve, 1500));
        return apiClient(originalRequest);
      }
      if (originalRequest?.__apvSyncTracked) {
        originalRequest.__apvSyncTracked = false;
        useSyncStore.getState().completeTask(false);
      }
      recordFailedRequest({
        method: originalRequest?.method || 'GET',
        url: String(originalRequest?.url || ''),
        status: 'NETWORK_ERROR',
        message: 'Không thể kết nối đến server',
      });
      console.error('Network Error: Không thể kết nối đến server');
    }
    return Promise.reject(error);
  }
);

// --- Generic CRUD functions ---

async function get<T>(endpoint: string, params?: Record<string, unknown>): Promise<ApiResponse<T>> {
  const response = await apiClient.get<ApiResponse<T>>(endpoint, { params });
  return response.data;
}

async function post<T>(endpoint: string, data?: unknown): Promise<ApiResponse<T>> {
  const response = await apiClient.post<ApiResponse<T>>(endpoint, data);
  return response.data;
}

async function put<T>(endpoint: string, data?: unknown): Promise<ApiResponse<T>> {
  const response = await apiClient.put<ApiResponse<T>>(endpoint, data);
  return response.data;
}

async function patch<T>(endpoint: string, data?: unknown): Promise<ApiResponse<T>> {
  const response = await apiClient.patch<ApiResponse<T>>(endpoint, data);
  return response.data;
}

async function del<T>(endpoint: string): Promise<ApiResponse<T>> {
  const response = await apiClient.delete<ApiResponse<T>>(endpoint);
  return response.data;
}

// --- Customers ---
export const customersApi = {
  getAll: (params?: { page?: number; limit?: number; search?: string; type?: string }) =>
    get<PaginatedData<unknown>>('/customers', params),
  getById: (id: string) => get<unknown>(`/customers/${id}`),
  create: (data: unknown) => post<unknown>('/customers', data),
  update: (id: string, data: unknown) => put<unknown>(`/customers/${id}`, data),
  delete: (id: string) => del<null>(`/customers/${id}`),
};

// --- Products ---
export const productsApi = {
  getAll: (params?: { page?: number; limit?: number; search?: string; type?: string; customerId?: string }) =>
    get<PaginatedData<unknown>>('/products', params),
  getById: (id: string) => get<unknown>(`/products/${id}`),
  create: (data: unknown) => post<unknown>('/products', data),
  update: (id: string, data: unknown) => put<unknown>(`/products/${id}`, data),
  delete: (id: string) => del<null>(`/products/${id}`),
  estimateCost: (id: string, quantity: number) => post<{
    success: boolean;
    quantity: number;
    unitCost: number;
    totalCost: number;
    totalMaterialCost: number;
    totalProcessCost: number;
    outsourceCost: number;
  }>(`/products/${id}/estimate-cost`, { quantity }),
};

// --- Materials ---
export const materialsApi = {
  getAll: (params?: { page?: number; limit?: number; search?: string; category?: string }) =>
    get<PaginatedData<unknown>>('/materials', params),
  getById: (id: string) => get<unknown>(`/materials/${id}`),
  create: (data: unknown) => post<unknown>('/materials', data),
  update: (id: string, data: unknown) => put<unknown>(`/materials/${id}`, data),
  delete: (id: string) => del<null>(`/materials/${id}`),
};

// --- Users ---
export const usersApi = {
  getAll: (params?: { page?: number; limit?: number; search?: string; role?: string; status?: string }) =>
    get<PaginatedData<unknown>>('/users', params),
  getById: (id: string) => get<unknown>(`/users/${id}`),
  create: (data: unknown) => post<unknown>('/users', data),
  update: (id: string, data: unknown) => put<unknown>(`/users/${id}`, data),
  delete: (id: string) => del<null>(`/users/${id}`),
};

// --- Roles ---
export const rolesApi = {
  getAll: () => get<unknown[]>('/roles'),
  getById: (id: string) => get<unknown>(`/roles/${id}`),
  create: (data: unknown) => post<unknown>('/roles', data),
  update: (id: string, data: unknown) => put<unknown>(`/roles/${id}`, data),
  delete: (id: string) => del<null>(`/roles/${id}`),
};

// --- Machines ---
export const machinesApi = {
  getAll: (params?: { page?: number; limit?: number; search?: string; type?: string; status?: string }) =>
    get<PaginatedData<unknown>>('/machines', params),
  getDashboard: () => get<unknown>('/machines/dashboard'),
  getById: (id: string) => get<unknown>(`/machines/${id}`),
  create: (data: unknown) => post<unknown>('/machines', data),
  update: (id: string, data: unknown) => put<unknown>(`/machines/${id}`, data),
  updateStatus: (id: string, status: string) => patch<unknown>(`/machines/${id}/status`, { status }),
  delete: (id: string) => del<null>(`/machines/${id}`),
};

// --- Quotations ---
export const quotationsApi = {
  getAll: (params?: { page?: number; limit?: number; search?: string; status?: string; customerId?: string }) =>
    get<PaginatedData<unknown>>('/quotations', params),
  getById: (id: string) => get<unknown>(`/quotations/${id}`),
  create: (data: unknown) => post<unknown>('/quotations', data),
  update: (id: string, data: unknown) => put<unknown>(`/quotations/${id}`, data),
  updateStatus: (id: string, status: string) => patch<unknown>(`/quotations/${id}/status`, { status }),
  delete: (id: string) => del<null>(`/quotations/${id}`),
};

// --- Orders ---
export const ordersApi = {
  getAll: (params?: { page?: number; limit?: number; search?: string; status?: string; customerId?: string }) =>
    get<PaginatedData<unknown>>('/orders', params),
  getById: (id: string) => get<unknown>(`/orders/${id}`),
  create: (data: unknown) => post<unknown>('/orders', data),
  update: (id: string, data: unknown) => put<unknown>(`/orders/${id}`, data),
  updateStatus: (id: string, status: string, userId?: string) => patch<unknown>(`/orders/${id}/status`, { status, userId }),
  delete: (id: string) => del<null>(`/orders/${id}`),
};

// --- Production Orders ---
export const productionOrdersApi = {
  getAll: (params?: { page?: number; limit?: number; search?: string; status?: string; priority?: string }) =>
    get<PaginatedData<unknown>>('/production-orders', params),
  getById: (id: string) => get<unknown>(`/production-orders/${id}`),
  create: (data: unknown) => post<unknown>('/production-orders', data),
  update: (id: string, data: unknown) => put<unknown>(`/production-orders/${id}`, data),
  addRoutingStep: (id: string, data: unknown) => post<unknown>(`/production-orders/${id}/routing`, data),
  updateStatus: (id: string, status: string) => patch<unknown>(`/production-orders/${id}/status`, { status }),
  delete: (id: string) => del<null>(`/production-orders/${id}`),
};

// --- Machine Logs ---
export const machineLogsApi = {
  getAll: (params?: { page?: number; limit?: number; machineId?: string; type?: string; date?: string }) =>
    get<PaginatedData<unknown>>('/machine-logs', params),
  getById: (id: string) => get<unknown>(`/machine-logs/${id}`),
  create: (data: unknown) => post<unknown>('/machine-logs', data),
  update: (id: string, data: unknown) => put<unknown>(`/machine-logs/${id}`, data),
  delete: (id: string) => del<null>(`/machine-logs/${id}`),
};

// --- Production Logs ---
export const productionLogsApi = {
  getAll: (params?: { page?: number; limit?: number; machineId?: string; status?: string; productionOrderId?: string }) =>
    get<PaginatedData<unknown>>('/production-logs', params),
  getById: (id: string) => get<unknown>(`/production-logs/${id}`),
  create: (data: unknown) => post<unknown>('/production-logs', data),
  update: (id: string, data: unknown) => put<unknown>(`/production-logs/${id}`, data),
  delete: (id: string) => del<null>(`/production-logs/${id}`),
};

// --- WIP Transfers ---
export const wipTransfersApi = {
  getAll: (params?: { page?: number; limit?: number; productionOrderId?: string; status?: string }) =>
    get<PaginatedData<unknown>>('/wip-transfers', params),
  getById: (id: string) => get<unknown>(`/wip-transfers/${id}`),
  // Ghi phiếu chuyển BTP qua tRPC wipTransfer (đường REST ghi đã gỡ)
};

// --- QC ---
export const qcApi = {
  // QC Checks
  getChecks: (params?: { page?: number; limit?: number; status?: string }) =>
    get<PaginatedData<unknown>>('/qc/checks', params),
  getCheck: (id: string) => get<unknown>(`/qc/checks/${id}`),
  createCheck: (data: unknown) => post<unknown>('/qc/checks', data),

  // QC Process
  getProcesses: (params?: { page?: number; limit?: number; status?: string; productionOrderId?: string }) =>
    get<PaginatedData<unknown>>('/qc/process', params),
  getProcess: (id: string) => get<unknown>(`/qc/process/${id}`),
  createProcess: (data: unknown) => post<unknown>('/qc/process', data),

  // QC Materials
  getMaterials: (params?: { page?: number; limit?: number; status?: string }) =>
    get<PaginatedData<unknown>>('/qc/materials', params),
  getMaterial: (id: string) => get<unknown>(`/qc/materials/${id}`),
  createMaterial: (data: unknown) => post<unknown>('/qc/materials', data),

  // QC Final
  getFinals: (params?: { page?: number; limit?: number; status?: string }) =>
    get<PaginatedData<unknown>>('/qc/final', params),
  getFinal: (id: string) => get<unknown>(`/qc/final/${id}`),
  createFinal: (data: unknown) => post<unknown>('/qc/final', data),

  // NCR
  getNCRs: (params?: { page?: number; limit?: number; status?: string; severity?: string }) =>
    get<PaginatedData<unknown>>('/qc/ncr', params),
  getNCR: (id: string) => get<unknown>(`/qc/ncr/${id}`),
  createNCR: (data: unknown) => post<unknown>('/qc/ncr', data),
  updateNCR: (id: string, data: unknown) => put<unknown>(`/qc/ncr/${id}`, data),
};

// --- QC Materials ---
export const qcMaterialsApi = {
  getAll: (params?: { page?: number; limit?: number; status?: string }) =>
    get<PaginatedData<unknown>>('/qc/materials', params),
  getById: (id: string) => get<unknown>(`/qc/materials/${id}`),
  create: (data: unknown) => post<unknown>('/qc/materials', data),
  update: (id: string, data: unknown) => put<unknown>(`/qc/materials/${id}`, data),
  delete: (id: string) => del<null>(`/qc/materials/${id}`),
};

// --- QC Process ---
export const qcProcessApi = {
  getAll: (params?: { page?: number; limit?: number; status?: string; productionOrderId?: string }) =>
    get<PaginatedData<unknown>>('/qc/process', params),
  getById: (id: string) => get<unknown>(`/qc/process/${id}`),
  create: (data: unknown) => post<unknown>('/qc/process', data),
  update: (id: string, data: unknown) => put<unknown>(`/qc/process/${id}`, data),
  delete: (id: string) => del<null>(`/qc/process/${id}`),
};

// --- QC Final ---
export const qcFinalApi = {
  getAll: (params?: { page?: number; limit?: number; status?: string }) =>
    get<PaginatedData<unknown>>('/qc/final', params),
  getById: (id: string) => get<unknown>(`/qc/final/${id}`),
  create: (data: unknown) => post<unknown>('/qc/final', data),
  update: (id: string, data: unknown) => put<unknown>(`/qc/final/${id}`, data),
  delete: (id: string) => del<null>(`/qc/final/${id}`),
};

// --- QC Checks ---
export const qcChecksApi = {
  getAll: (params?: { page?: number; limit?: number; status?: string }) =>
    get<PaginatedData<unknown>>('/qc/checks', params),
  getById: (id: string) => get<unknown>(`/qc/checks/${id}`),
  create: (data: unknown) => post<unknown>('/qc/checks', data),
  update: (id: string, data: unknown) => put<unknown>(`/qc/checks/${id}`, data),
  delete: (id: string) => del<null>(`/qc/checks/${id}`),
};

// --- NCR ---
export const ncrApi = {
  getAll: (params?: { page?: number; limit?: number; status?: string; severity?: string }) =>
    get<PaginatedData<unknown>>('/qc/ncr', params),
  getById: (id: string) => get<unknown>(`/qc/ncr/${id}`),
  create: (data: unknown) => post<unknown>('/qc/ncr', data),
  update: (id: string, data: unknown) => put<unknown>(`/qc/ncr/${id}`, data),
  delete: (id: string) => del<null>(`/qc/ncr/${id}`),
};

// --- Vendors ---
export const vendorsApi = {
  getAll: (params?: { page?: number; limit?: number; search?: string; type?: string }) =>
    get<PaginatedData<unknown>>('/vendors', params),
  getById: (id: string) => get<unknown>(`/vendors/${id}`),
  create: (data: unknown) => post<unknown>('/vendors', data),
  update: (id: string, data: unknown) => put<unknown>(`/vendors/${id}`, data),
  delete: (id: string) => del<null>(`/vendors/${id}`),
};

// --- Outsource POs ---
export const outsourcePOsApi = {
  getAll: (params?: { page?: number; limit?: number; status?: string; vendorId?: string }) =>
    get<PaginatedData<unknown>>('/outsource-pos', params),
  getById: (id: string) => get<unknown>(`/outsource-pos/${id}`),
  create: (data: unknown) => post<unknown>('/outsource-pos', data),
  update: (id: string, data: unknown) => put<unknown>(`/outsource-pos/${id}`, data),
  updateStatus: (id: string, status: string) => patch<unknown>(`/outsource-pos/${id}/status`, { status }),
  delete: (id: string) => del<null>(`/outsource-pos/${id}`),
};

// Alias for consistency with naming convention
export const outsourcePosApi = outsourcePOsApi;



// --- Purchase Orders ---
export const purchaseOrdersApi = {
  getAll: (params?: { page?: number; limit?: number; status?: string; vendorId?: string }) =>
    get<PaginatedData<unknown>>('/purchase-orders', params),
  getById: (id: string) => get<unknown>(`/purchase-orders/${id}`),
  create: (data: unknown) => post<unknown>('/purchase-orders', data),
  update: (id: string, data: unknown) => put<unknown>(`/purchase-orders/${id}`, data),
  updateStatus: (id: string, status: string) => patch<unknown>(`/purchase-orders/${id}/status`, { status }),
  delete: (id: string) => del<null>(`/purchase-orders/${id}`),
};

// --- Deliveries ---
export const deliveriesApi = {
  getAll: (params?: { page?: number; limit?: number; status?: string; customerId?: string }) =>
    get<PaginatedData<unknown>>('/deliveries', params),
  getById: (id: string) => get<unknown>(`/deliveries/${id}`),
  create: (data: unknown) => post<unknown>('/deliveries', data),
  update: (id: string, data: unknown) => put<unknown>(`/deliveries/${id}`, data),
  addItem: (id: string, data: unknown) => post<unknown>(`/deliveries/${id}/items`, data),
  updateStatus: (id: string, status: string) => patch<unknown>(`/deliveries/${id}/status`, { status }),
  delete: (id: string) => del<null>(`/deliveries/${id}`),
};

// --- Technical ---
export const technicalApi = {
  // Specs
  getSpecs: (params?: { page?: number; limit?: number; search?: string }) =>
    get<PaginatedData<unknown>>('/technical/specs', params),
  getSpec: (id: string) => get<unknown>(`/technical/specs/${id}`),
  createSpec: (data: unknown) => post<unknown>('/technical/specs', data),
  updateSpec: (id: string, data: unknown) => put<unknown>(`/technical/specs/${id}`, data),
  deleteSpec: (id: string) => del<null>(`/technical/specs/${id}`),

  // Plates
  getPlates: (params?: { page?: number; limit?: number; search?: string; status?: string }) =>
    get<PaginatedData<unknown>>('/technical/plates', params),
  getPlate: (id: string) => get<unknown>(`/technical/plates/${id}`),
  lookupPlates: (params?: { productId?: string; customerId?: string }) =>
    get<unknown[]>('/technical/plates/lookup', params),
  createPlate: (data: unknown) => post<unknown>('/technical/plates', data),
  updatePlate: (id: string, data: unknown) => put<unknown>(`/technical/plates/${id}`, data),
  deletePlate: (id: string) => del<null>(`/technical/plates/${id}`),

  // Die Cuts
  getDieCuts: (params?: { page?: number; limit?: number; search?: string; status?: string }) =>
    get<PaginatedData<unknown>>('/technical/molds', params),
  getDieCut: (id: string) => get<unknown>(`/technical/molds/${id}`),
  lookupDieCuts: (params?: { productId?: string; customerId?: string }) =>
    get<unknown[]>('/technical/molds/lookup', params),
  createDieCut: (data: unknown) => post<unknown>('/technical/molds', data),
  updateDieCut: (id: string, data: unknown) => put<unknown>(`/technical/molds/${id}`, data),
  deleteDieCut: (id: string) => del<null>(`/technical/molds/${id}`),

  // Layouts
  getLayouts: (params?: { page?: number; limit?: number; search?: string; status?: string }) =>
    get<PaginatedData<unknown>>('/technical/layouts', params),
  getLayout: (id: string) => get<unknown>(`/technical/layouts/${id}`),
  createLayout: (data: unknown) => post<unknown>('/technical/layouts', data),
  updateLayout: (id: string, data: unknown) => put<unknown>(`/technical/layouts/${id}`, data),
  deleteLayout: (id: string) => del<null>(`/technical/layouts/${id}`),

  // Processes
  getProcesses: () => get<unknown[]>('/technical/processes'),
  getProcess: (id: string) => get<unknown>(`/technical/processes/${id}`),
  createProcess: (data: unknown) => post<unknown>('/technical/processes', data),
  updateProcess: (id: string, data: unknown) => put<unknown>(`/technical/processes/${id}`, data),
  deleteProcess: (id: string) => del<null>(`/technical/processes/${id}`),
};



// --- Activity Log ---
export const activityLogApi = {
  getByEntity: (entityType: string, entityId: string, cursor?: string) =>
    get<{items: unknown[], nextCursor?: string}>(`/activity-log/${entityType}/${entityId}${cursor ? `?cursor=${cursor}` : ''}`),
  log: (data: {
    entityType: string;
    entityId: string;
    action: string;
    field?: string;
    oldValue?: string;
    newValue?: string;
    description?: string;
    userId?: string;
  }) => post<unknown>('/activity-log', data),
};

// --- Auth ---
export const authApi = {
  login: (data: { email: string; password: string }) =>
    post<{ user: unknown; token: string; refreshToken: string; expiresIn: string }>('/auth/login', data),
  logout: () => post<null>('/auth/logout'),
  me: () => get<unknown>('/auth/me'),
  forgotPassword: (data: { email: string }) =>
    post<{ message: string }>('/auth/forgot-password', data),
  resetPassword: (data: { token: string; password: string }) =>
    post<{ message: string }>('/auth/reset-password', data),
  refreshToken: (refreshToken: string) =>
    post<{ token: string; refreshToken: string; expiresIn: string }>('/auth/refresh-token', { refreshToken }),
  getActiveSessions: () => get<unknown[]>('/auth/sessions'),
  getUserActivity: (limit?: number) => get<unknown[]>('/auth/user-activity', { limit }),
};

// --- Config ---
export const configApi = {
  getAll: () => get<unknown>('/config'),
  getByKey: (key: string) => get<unknown>(`/config/${key}`),
  update: (key: string, value: unknown) => put<unknown>(`/config/${key}`, { value }),
  getCategories: () => get<unknown[]>('/config/categories'),
  getProcessLibrary: () => get<unknown[]>('/config/processes'),
};



// --- Material Planning ---
export const materialPlanningApi = {
  getAll: (params?: { page?: number; limit?: number; status?: string }) =>
    get<PaginatedData<unknown>>('/material-planning', params),
  getById: (id: string) => get<unknown>(`/material-planning/${id}`),
  create: (data: unknown) => post<unknown>('/material-planning', data),
  update: (id: string, data: unknown) => put<unknown>(`/material-planning/${id}`, data),
  delete: (id: string) => del<null>(`/material-planning/${id}`),
};

// --- Dashboard ---
export const dashboardApi = {
  getAnalytics: (params?: { period?: string }) =>
    get<unknown>('/dashboard/analytics', params),
};

// --- Notifications ---
export const notificationsApi = {
  getAll: (params?: { page?: number; limit?: number }) =>
    get<PaginatedData<unknown>>('/notifications', params),
  markAsRead: (id: string) => patch<unknown>(`/notifications/${id}/read`, {}),
  markAllRead: () => patch<unknown>('/notifications/read-all', {}),
};

// --- Configs (Dynamic Labels) ---
export const configsApi = {
  /** Get all categories with their items (for prefetch) */
  getCategories: (section?: string) =>
    get<any[]>('/configs/categories', section ? { section } : undefined),
  /** Get items for a specific category */
  getItems: (code: string) =>
    get<any[]>(`/configs/${code}/items`),
};

// --- Upload ---
export const uploadApi = {
  uploadFile: async (file: File) => {
    if (file.size > MAX_ATTACHMENT_SIZE) {
      throw new Error(
        `Tệp "${file.name}" (${formatBytes(file.size)}) vượt quá dung lượng tối đa cho phép (tối đa 100MB). Vui lòng nén file hoặc chia sẻ liên kết Google Drive.`
      );
    }

    const formData = new FormData();
    formData.append('file', file);
    
    const token = useAuthStore.getState().accessToken;
    useSyncStore.getState().beginTask('Đang tải tệp lên...');
    let response: Response;
    try {
      response = await fetch(`${API_BASE_URL}/upload`, {
        method: 'POST',
        headers: {
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: formData
      });
    } catch (networkErr: unknown) {
      useSyncStore.getState().completeTask(false);
      const errObj = networkErr as { name?: string; message?: string } | null;
      if (errObj?.name === 'TypeError' || errObj?.message === 'Failed to fetch') {
        throw new Error('Không thể kết nối tải tệp lên máy chủ hoặc tệp bị chặn bởi kết nối mạng. Vui lòng thử lại hoặc giảm kích thước tệp.');
      }
      throw new Error(extractErrorMessage(networkErr, 'Lỗi kết nối tải tệp lên máy chủ'));
    }
    
    if (!response.ok) {
      useSyncStore.getState().completeTask(false);
      const errJson = await response.json().catch(() => null);
      const errMsg = errJson?.message || errJson?.error || `Upload failed: ${response.statusText} (${response.status})`;
      throw new Error(errMsg);
    }
    
    const envelope: ApiResponse<{ fileUrl: string; fileName: string }> = await response.json();
    useSyncStore.getState().completeTask(true, 'Đã tải tệp lên');
    const result = envelope.data ?? { fileUrl: '', fileName: '' };
    if (result.fileUrl) {
        result.fileUrl = getUploadUrl(result.fileUrl);
    }
    return result;
  },
};

// --- Process Planning ---
export const processPlanningApi = {
  getOrders: (params?: { status?: string; machineId?: string; startDate?: string; endDate?: string }) =>
    get<unknown>('/process-planning/orders', params),
  getMachines: (params?: { status?: string; startDate?: string; endDate?: string }) =>
    get<unknown>('/process-planning/machines', params),
  updateRoutingStep: (id: string, data: { resourceId?: string | null; operatorId?: string | null; shiftType?: string | null; plannedStartDate?: string | null; plannedEndDate?: string | null }) =>
    put<unknown>(`/process-planning/routing-step/${id}`, data),
};

export default apiClient;
